import * as React from 'react';
import { connect } from 'react-redux';
import {WithStyles, withStyles, MenuItem, Dialog, DialogTitle, DialogContent, IconButton, Box} from '@material-ui/core';
import TranslateNew from '@translate/TranslateNew';
import { HeaderStyle } from './Header.style';
import { ILogEntry, Action } from '@app-types/websocket/log.types';
import { IEvent } from '@app-types/websocket/event.types';
import { ITodo } from '@app-types/websocket/todo.types';
import { ISuggestion } from '@app-types/websocket/suggestion.types';
import { IFamilyMember } from '@app-types/websocket/family.types';
import ImgAvatar from '@components/common/avatar/ImgAvatar';
import { IFlowPost} from '@app-types/websocket/imageflow.types';
import { DialogTop } from '@components/economy/DialogComponens';
import { Close } from '@material-ui/icons';
import { RouteComponentProps, withRouter } from 'react-router';
import { ISchedule } from '@app-types/websocket/schedule.types';
import Button from '@components/common/button/Button';
import { IEcoPurchase, IEcoTransfer } from '@app-types/websocket/economy.types';
import { ISharedNote } from '@app-types/websocket/sharedinfo.types';
import ShowChanges from '../suggestion/ShowChanges';
import { FamilyMemberType } from '@services/family/family.constants';

const tn: TranslateNew = TranslateNew.getInstance();

interface IProps extends WithStyles<typeof HeaderStyle>, RouteComponentProps {
  logEntries: ILogEntry[];
  events: IEvent[];
  todos: ITodo[];
  suggestions: ISuggestion[];
  family: IFamilyMember[];
  posts: IFlowPost[];
  schedules: ISchedule[];
  purchases: IEcoPurchase[];
  transfers: IEcoTransfer[];
  sharedNotes: ISharedNote[];

  open: boolean;
  setSelectedDate: (year: number, month: number, date: number) => void;
  close: () => void;

  userId: number;
}

interface IState {
  itemsToShow: number;
  showSuggestionDetails: ISuggestion | undefined;
}

class ActivityMenu extends React.Component<IProps, IState> {

  constructor(props: IProps) {
    super(props);
    this.state = {
      itemsToShow: 15,
      showSuggestionDetails:undefined,
    };

    this.handleShowMore = this.handleShowMore.bind(this);
  }

  public render() {
    const { logEntries, open, close } = this.props;
    const { itemsToShow } = this.state;

    const entries: JSX.Element[] = [];

    let myRole:FamilyMemberType = FamilyMemberType.VIEWER;
    for (const mem of this.props.family) {
      if (mem.id === this.props.userId) {
        myRole = mem.role;
        break;
      }
    }

    let prevLogEntry: ILogEntry | undefined;
    for (const logEntry of logEntries) {
      if (prevLogEntry && prevLogEntry.accountId === logEntry.accountId 
        && prevLogEntry.action === logEntry.action 
        && prevLogEntry.object === logEntry.object) {
        // Skip duplicate log entries
        continue;
      }
      const entry: ((JSX.Element) | null) = this.renderLogEntry(logEntry, myRole);
      if (entry) {
        entries.push(entry);
      }
      prevLogEntry = logEntry;
    }

    const displayedEntries = entries.slice(0, itemsToShow);

    return (<>
      <Dialog open={open} onClose={close} fullWidth={true}>
        <DialogTop>
          <DialogTitle style={{ marginLeft: "40px" }}>{tn.lang.activityLog}</DialogTitle>
          <IconButton onClick={close}>
            <Close />
          </IconButton>
        </DialogTop>
        <DialogContent> {displayedEntries}
          {entries.length > itemsToShow && (
            <Box textAlign="center" mt={2}>
              <Button onClick={this.handleShowMore} variant="contained">
                {tn.lang.showMore}
              </Button>
            </Box>
          )}</DialogContent>
      </Dialog>
      <ShowChanges // this is a popup to show changes of selected change
        open={this.state.showSuggestionDetails !== undefined}
        close={() => this.setState({showSuggestionDetails:undefined})}
        suggestion={this.state.showSuggestionDetails}
      />
      </>
    );
  }

  private renderLogEntry(logEntry: ILogEntry, myRole:FamilyMemberType): JSX.Element | null {
    const { classes } = this.props;

    let actionText: string | JSX.Element | undefined;
    let imagePreview: JSX.Element | undefined;
    let clickAction: (() => void) | undefined;

    if (myRole !== FamilyMemberType.PARENT && myRole !== FamilyMemberType.MODERATOR) {
      if (logEntry.action === Action.SUGGESTION_CREATED 
        || logEntry.action === Action.SUGGESTION_REJECTED
        || logEntry.action === Action.AGREEMENT_SUGGESTED
        || logEntry.action === Action.AGREEMENT_RESPONDED
        || logEntry.action === Action.ECO_PURCHASE_CREATED
        || logEntry.action === Action.ECO_PURCHASE_CHANGE
        || logEntry.action === Action.ECO_TRANSFER_CREATED) {
          return null;
        }
    }

    switch (logEntry.action) {
      case Action.SUGGESTION_ACCEPTED: {
        const suggestion: ISuggestion | undefined = this.getSuggestion(logEntry.object);
        actionText = tn.lang.eventRecentActionSuggestionAccepted(logEntry.object);
        if (suggestion) {
          clickAction = () => {this.setState({showSuggestionDetails:suggestion})};
        }
        break;
      } case Action.SUGGESTION_CREATED: {
        const suggestion: ISuggestion | undefined = this.getSuggestion(logEntry.object);
        actionText = tn.lang.eventRecentActionSuggestionCreated(logEntry.object);
        if (suggestion) {
          clickAction = () => {this.setState({showSuggestionDetails:suggestion})};
        }
        break;
      } case Action.SUGGESTION_REJECTED: {
        const suggestion: ISuggestion | undefined = this.getSuggestion(logEntry.object);
        actionText = tn.lang.eventRecentActionSuggestionRejected(logEntry.object);
        if (suggestion) {
          clickAction = () => {this.setState({showSuggestionDetails:suggestion})};
        }
        break;
      } case Action.EVENT_CREATED: {
        const event: IEvent | undefined = this.getEvent(logEntry.object);
        const title: string = event ? event.title : ("#" + logEntry.object);
        if (event) {
          const startTime: Date = new Date(event.startTime);
          clickAction = () => {this.props.setSelectedDate(startTime.getFullYear(), startTime.getMonth(), startTime.getDate())};
        }
        actionText = tn.lang.eventRecentActionEventCreated(title);
        break;
      } case Action.EVENT_GROUP_CREATED: {
        const event: IEvent | undefined = this.getEvent(logEntry.object);
        const title: string = event ? event.title : ("#" + logEntry.object);
        if (event) {
          const startTime: Date = new Date(event.startTime);
          clickAction = () => {this.props.setSelectedDate(startTime.getFullYear(), startTime.getMonth(), startTime.getDate())};
        }
        actionText = tn.lang.eventRecentActionEventGroupCreated(title);
        break;
      } case Action.EVENT_MODIFIED: {
        const event: IEvent | undefined = this.getEvent(logEntry.object);
        const title: string = event ? event.title : ("#" + logEntry.object);
        if (event) {
          const startTime: Date = new Date(event.startTime);
          clickAction = () => {this.props.setSelectedDate(startTime.getFullYear(), startTime.getMonth(), startTime.getDate())};
        }
        actionText = tn.lang.eventRecentActionEventModified(title);
        break;
      } case Action.EVENT_GROUP_MODIFIED: {
        const event: IEvent | undefined = this.getEvent(logEntry.object);
        const title: string = event ? event.title : ("#" + logEntry.object);
        if (event) {
          const startTime: Date = new Date(event.startTime);
          clickAction = () => {this.props.setSelectedDate(startTime.getFullYear(), startTime.getMonth(), startTime.getDate())};
        }
        actionText = tn.lang.eventRecentActionEventGroupModified(title);
        break;
      } case Action.EVENT_DELETED: {
        const event: IEvent | undefined = this.getEvent(logEntry.object);
        const title: string = event ? event.title : ("#" + logEntry.object);
        actionText = tn.lang.eventRecentActionEventDeleted(title);
        break;
      } case Action.EVENT_GROUP_DELETED: {
        const event: IEvent | undefined = this.getEvent(logEntry.object);
        const title: string = event ? event.title : ("#" + logEntry.object);
        actionText = tn.lang.eventRecentActionEventGroupDeleted(title);
        break;
      } case Action.TODO_CREATED: {
        const todo: ITodo | undefined = this.getTodo(logEntry.object);
        const title: string = todo ? todo.title : ("#" + logEntry.object);
        clickAction = () => {this.props.history.push(`/app/notes/2`)};
        actionText = tn.lang.eventRecentActionTodoCreated(title);
        
        break;
      } case Action.TODO_MODIFIED: {
        const todo: ITodo | undefined = this.getTodo(logEntry.object);
        const title: string = todo ? todo.title : ("#" + logEntry.object);
        clickAction = () => {this.props.history.push(`/app/notes/2`)};
        actionText = tn.lang.eventRecentActionTodoModified(title);
        break;
      } case Action.TODO_DELETED: {
        const todo: ITodo | undefined = this.getTodo(logEntry.object);
        const title: string = todo ? todo.title : ("#" + logEntry.object);
        actionText = tn.lang.eventRecentActionTodoDeleted(title);
        clickAction = () => {this.props.history.push(`/app/notes/2`)};
        break;
      } case Action.FLOW_POST_CREATED: {
        const image: IFlowPost | undefined = this.getImage(logEntry.object);
        const title: string = image ? image.fileDesc.fileName : ("#" + logEntry.object);
        clickAction = () => {this.props.history.push(`/app/info/:tab`)};
        actionText = tn.lang.flowPostRecentActionImageUploaded
        if (image) {
          imagePreview = (
            <img
              src={image.fileDesc.url}
              alt={title}
              className={classes.imagePreview}
              onClick={() => this.props.history.push(`/app/info/:tab`)}
            />);
        }
        break;
      } case Action.ECO_PURCHASE_CREATED: {
        const purchase: IEcoPurchase | undefined = this.getPurchase(logEntry.object);
        const title: string = purchase ? purchase.title : ("#" + logEntry.object);
        actionText = tn.lang.ecoRecentActionPurchaseCreated(title);
        clickAction = () => {this.props.history.push(`/app/economy/:transactionId`)};
        break;
      } case Action.ECO_PURCHASE_CHANGE: {
        const purchase: IEcoPurchase | undefined = this.getPurchase(logEntry.object);
        const title: string = purchase ? purchase.title : ("#" + logEntry.object);
        actionText = tn.lang.ecoRecentActionPurchaseChanged(title);
        clickAction = () => {this.props.history.push(`/app/economy/:transactionId`)};
        break;
      } case Action.ECO_TRANSFER_CREATED: {
        const transfer: IEcoTransfer | undefined = this.getTransfer(logEntry.object);
        const sum: number = transfer ? transfer.sum : (logEntry.object);
        const currency: string = transfer ? transfer.currency : "";
        actionText = tn.lang.ecoRecentActionNewTransferCreated(sum, currency);
        clickAction = () => {this.props.history.push(`/app/economy/:transactionId`)};
        break;
      } case Action.SHARED_NOTE_CREATED: {
        const note: ISharedNote | undefined = this.getNotes(logEntry.object);
        const title: string = note ? note.serviceName : ("#" + logEntry.object);
        actionText = tn.lang.sharedNoteCreated(title);
        clickAction = () => {this.props.history.push(`/app/notes/1`)};
        break;
      } case Action.SHARED_NOTE_MODIFIED: {
        const Note: ISharedNote | undefined = this.getNotes(logEntry.object);
        const title: string = Note ? Note.serviceName : ("#" + logEntry.object);
        actionText = tn.lang.sharedNoteModified(title);
        clickAction = () => {this.props.history.push(`/app/notes/1`)};
        break;
      }
    }

    const person: IFamilyMember | undefined = this.props.family.find((mem) => mem.id === logEntry.accountId);
    const time = tn.lang.momentFromNow(logEntry.time);

    if (actionText) {
      return (<MenuItem
        key={logEntry.id}
        className={classes.menuListItem}
        style={{ padding: '10px' }}
        disabled={(clickAction === undefined )}
        onClick={() => {if (clickAction !== undefined) {this.props.close(); clickAction();}}}>

        {person && <ImgAvatar altText={person.firstName} imgUrl={person.imageAvatar ? person.avatarURL : undefined}
          imgName={person.avatar} style={{ width: '55px', height: '55px', marginRight: '15px' }} color={person.color} />}

        <div className={classes.activityLog}>
          <span>
            {person?.firstName} {actionText}
          </span>
          <span className={classes.menuItemTime}>
            {time}
          </span>
        </div>
        {imagePreview}
      </MenuItem>)
    }
    return null;
  }


  private getEvent(id: number | undefined): IEvent | undefined {
    if (id === undefined) {
      return undefined;
    }
    for (const event of this.props.events) {
      if (event.id === id) {
        return event;
      }
    }
    return undefined;
  }

  private getSuggestion(id: number | undefined): ISuggestion | undefined {
    if (id === undefined) {
      return undefined;
    }
    for (const suggestion of this.props.suggestions) {
      if (suggestion.id === id) {
        return suggestion;
      }
    }
    return undefined;
  }

  private getTodo(id: number | undefined): ITodo | undefined {
    if (id === undefined) {
      return undefined;
    }
    for (const todo of this.props.todos) {
      if (todo.id === id) {
        return todo;
      }
    }
    return undefined;
  }

  // do we need to show images we upload ourselves?
  private getImage(id: number | undefined): IFlowPost | undefined {
    if (id === undefined) {
      return undefined;
    }
    for (const image of this.props.posts) {
      if (image.postId === id) {
        return image;
      }
    }
    return undefined;
  }

  private getNotes(id: number | undefined): ISharedNote | undefined {
    if (id === undefined) {
      return undefined;
    }
    for (const note of this.props.sharedNotes) {
      if (note.noteId === id) {
        return note;
      }
    }
    return undefined;
  }


  private getPurchase(id: number | undefined): IEcoPurchase | undefined {
    if (id === undefined) {
      return undefined;
    }
    for (const transfer of this.props.purchases) {
      if (transfer.id === id) {
        return transfer;
      }
    }
    return undefined;
  }

  private getTransfer(id: number | undefined): IEcoTransfer | undefined {
    if (id === undefined) {
      return undefined;
    }
    for (const transfer of this.props.transfers) {
      if (transfer.id === id) {
        return transfer;
      }
    }
    return undefined;
  }

  private handleShowMore = () => {
    this.setState({ itemsToShow: this.state.itemsToShow + 10 });
  }


}


function mapStateToProps(state: any) {
  const logEntries: ILogEntry[] = state.logs.logEntries ? state.logs.logEntries : [];
  const events: IEvent[] = state.event.events ? state.event.events : [];
  const todos: ITodo[] = state.todo.todos ? state.todo.todos : [];
  const suggestions: ISuggestion[] = state.suggestion.suggestions ? state.suggestion.suggestions : [];
  const userId = state.user.session.data.id;
  const posts: IFlowPost[] = state.posts.posts ?? [];
  const schedules: ISchedule[] = state.schedule.schedules;
  const purchases: IEcoPurchase[] = state.economy.purchases ? state.economy.purchases : [];
  const transfers: IEcoTransfer[] = state.economy.transfers ? state.economy.transfers : [];
  const sharedNotes: ISharedNote[] = state.notes.sharedNotes ? state.notes.sharedNotes : [];


  return {
    userId,
    logEntries,
    events,
    todos,
    suggestions,
    posts,
    schedules,
    purchases,
    transfers,
    sharedNotes,
    family: state.family.family,
  };
}

export default connect(mapStateToProps)(withRouter((withStyles(HeaderStyle)(ActivityMenu))));



// WEBPACK FOOTER //
// ./src/components/calendar2/header/ActivityMenu.tsx